import * as React from 'react';
import type { UseMutationResult } from '@tanstack/react-query';
import { ShieldAlert, UserPlus } from 'lucide-react';

import { useAuth } from '@/auth/auth-context';
import {
  useInviteCollaborator,
  useResendSupplierActivation,
  useRevokeCollaborator,
  useSupplierTeam,
  useSupplierTeamTokenStatus,
} from '@/api/use-supplier-team';
import {
  SupplierTeamApiError,
  type SupplierTeamMemberDto,
  type SupplierTeamRoleDto,
  type SupplierTeamStateDto,
  type TokenActivationStatusDto,
} from '@/api/supplier-team-client';
import {
  resendAffordance,
  resendFailureCode,
  resendFailureMessage,
} from '@/api/resend-activation-error';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { type StatusTone } from '@/components/ui/status-badge';
import { cn } from '@/lib/utils';

// =============================================================================
// team-screen.tsx — Page « Mes collaborateurs » FOURNISSEUR (DBU §3.7, F4) — redesign MASTER/DETAIL
// (repère graphique GATE_HUMAN 2026-09-12).
// =============================================================================
// Zone RÉSERVÉE au contact MASTER du SIRET (§3.7). Structure MASTER/DÉTAIL (les 13 points du mandat) :
//   • En-tête épuré : titre sans icône (DBU §3.7 en info-bulle) + sous-titre COMPTEUR RÉEL + CTA
//     « Inviter un collaborateur » (sans icône, aligné sur le HAUT du titre) ouvrant une MODALE in-house.
//   • Grille [360px_1fr] : à gauche une LISTE (recherche instantanée + chips exclusifs Tous/Actifs/
//     Révoqués), à droite la FICHE du contact sélectionné (1er ouvert par défaut).
//   • Fiche : nom serif + badge de rôle + email, action « Révoquer l'accès », trois tuiles
//     (STATUT DU CONTACT / LIEN ÉTABLISSEMENT / CONNEXION) et un bloc HISTORIQUE daté.
//
// AUTORITÉ = API : le siret + l'identité MASTER (invitant) viennent de la SESSION (invariant n°1) ;
// l'ACL MASTER-only, le mur légal domaine et la garde [F1] sont serveur. L'UI ne réinterprète AUCUNE
// règle métier — elle relaie l'affichage et les messages d'erreur.
//
// PÉRIMÈTRE (GATE_HUMAN 2026-09-12) : la GESTION DES RÔLES est une prérogative ADMIN — aucune action
// « Modifier le rôle » côté fournisseur. Seule la révocation (« Révoquer l'accès ») est offerte au
// MASTER, câblée sur la mutation EXISTANTE `revokeCollaborator` (garde [F1] côté UI : seul un
// collaborateur non révoqué).
//
// ÉTAT LOCAL uniquement (recherche, chip actif, contact sélectionné, ouverture du modal) : préférences
// d'AFFICHAGE tenues en mémoire React (useState), JAMAIS en localStorage (RULE_REF_006) ni en URL.
//
// WCAG 2.1 AA : recherche étiquetée (aria-label), chips aria-pressed, éléments de liste = <button>
// dont le nom accessible EST le nom du contact (aria-current pour la sélection), point de statut
// TOUJOURS doublé d'un libellé texte (jamais une couleur seule), modale accessible (focus-trap, Échap,
// focus rendu), rôle/statut portés par un LIBELLÉ TEXTE.

/** Libellé FR court d'un rôle (le TEXTE porte l'info ; la couleur n'est qu'un renfort — WCAG). */
function roleLabel(role: SupplierTeamRoleDto): string {
  return role === 'MASTER' ? 'Responsable' : 'Collaborateur';
}

/** Libellé FR + tone DS d'un statut de contact (renfort TEXTE, jamais une couleur seule — WCAG). */
function stateBadge(state: SupplierTeamStateDto): {
  label: string;
  tone: StatusTone;
} {
  switch (state) {
    case 'Actif':
      return { label: 'Actif', tone: 'active' };
    case 'Invite':
      return { label: 'Invité', tone: 'invited' };
    case 'Inactif':
      return { label: 'Révoqué', tone: 'inactive' };
    case 'ResignatureRequise':
      return { label: 'Re-signature requise', tone: 'suspended' };
  }
}

/**
 * Libellé FR + tone DS du STATUT DU LIEN d'activation (Envoyé/Expiré/Activé). Le TEXTE porte l'info ;
 * la couleur n'est qu'un renfort (WCAG). `null` (aucune identité émise) → rendu « Aucun lien » gris.
 */
function tokenStatusBadge(status: TokenActivationStatusDto): {
  label: string;
  tone: StatusTone;
} {
  switch (status) {
    case 'Envoye':
      return { label: 'Envoyé', tone: 'invited' };
    case 'Expire':
      return { label: 'Expiré', tone: 'refused' };
    case 'Active':
      return { label: 'Activé', tone: 'active' };
  }
}

/** Classe du point coloré (renfort visuel) porté par un tone DS de statut. */
const TONE_DOT_CLASS: Record<StatusTone, string> = {
  active: 'bg-status-active',
  suspended: 'bg-status-suspended',
  invited: 'bg-status-invited',
  inactive: 'bg-status-inactive',
  refused: 'bg-destructive',
};

/** « Prénom NOM » — prénom tel quel, nom en MAJUSCULES (poids 500 côté rendu). */
function fullName(member: SupplierTeamMemberDto): string {
  return `${member.prenom} ${member.nom.toUpperCase()}`;
}

/** Un contact est RÉVOQUÉ si son état est Inactif (point de statut vide vs plein). */
function isRevoked(member: SupplierTeamMemberDto): boolean {
  return member.state === 'Inactif';
}

/** Un collaborateur RÉVOCABLE : collaborateur non déjà révoqué (garde [F1] côté UI, autorité = API). */
function isRevocable(member: SupplierTeamMemberDto): boolean {
  return member.role === 'COLLABORATEUR' && member.state !== 'Inactif';
}

/** Normalise pour une recherche insensible à la casse ET aux accents (é ≈ e). */
function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLocaleLowerCase('fr');
}

/** Date FR longue (« 1 juillet 2026 »), pour la tuile CONNEXION. */
function formatLongDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/** Date FR courte mono (« 9 sept. 2026 »), pour l'historique. */
function formatShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function SupplierTeamScreen(): React.JSX.Element {
  const auth = useAuth();

  // §3.7 ROLE-GATING : la zone entière est réservée au MASTER (filtre UI ; ACL réelle côté API).
  if (auth.supplierRole !== 'MASTER') {
    return (
      <section className="mx-auto max-w-3xl px-4 py-10">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <ShieldAlert aria-hidden="true" className="h-5 w-5 text-muted-foreground" />
              <CardTitle>Zone réservée au responsable du compte</CardTitle>
            </div>
            <CardDescription>
              La gestion des collaborateurs est accessible uniquement au contact responsable
              (MASTER) de votre établissement (DBU §3.7).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Pour toute demande relative à votre équipe, rapprochez-vous du responsable de votre
              établissement.
            </p>
          </CardContent>
        </Card>
      </section>
    );
  }

  return <MasterTeamView />;
}

/** Vue MASTER : en-tête + grille MASTER/DÉTAIL (liste filtrable à gauche, fiche à droite). */
function MasterTeamView(): React.JSX.Element {
  const query = useSupplierTeam();
  const [isInviteOpen, setInviteOpen] = React.useState(false);
  const [invitedName, setInvitedName] = React.useState<string | null>(null);
  const inviteTriggerRef = React.useRef<HTMLButtonElement | null>(null);

  // R15-UX-02 — plafond de collaborateurs (autorité = API). Quand le plafond est atteint, l'action
  // d'invitation est DÉSACTIVÉE et EXPLIQUÉE (le 409 serveur reste le garde-fou dur). Pendant le
  // chargement (data indéfinie), on ne bloque JAMAIS l'action (fail-open UI).
  const capReached = query.data?.capReached === true;
  const capDescriptionId = 'invite-cap-explanation';

  const onInviteSuccess = (name: string): void => {
    setInvitedName(name);
    setInviteOpen(false);
  };

  return (
    <section className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-8">
      {/* Point 1-3 : titre SANS icône (DBU §3.7 en info-bulle), sous-titre COMPTEUR, CTA aligné en HAUT. */}
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <h1
            title="Périmètre réservé au responsable du compte (DBU §3.7)"
            className="font-serif text-2xl font-semibold tracking-tight"
          >
            Mes collaborateurs
          </h1>
          {query.data !== undefined ? (
            <TeamSubtitle
              contactCount={query.data.members.length}
              cap={query.data.collaboratorCap}
              liveCount={query.data.liveCollaboratorCount}
              reached={query.data.capReached}
            />
          ) : null}
        </div>
        <div className="flex flex-col items-stretch gap-1.5 sm:items-end">
          <Button
            ref={inviteTriggerRef}
            type="button"
            disabled={capReached}
            aria-describedby={capReached ? capDescriptionId : undefined}
            onClick={() => {
              setInvitedName(null);
              setInviteOpen(true);
            }}
            className="shrink-0"
          >
            Inviter un collaborateur
          </Button>
          {capReached ? (
            <p
              id={capDescriptionId}
              role="status"
              className="max-w-xs text-xs text-muted-foreground sm:text-right"
            >
              Plafond de collaborateurs atteint. Révoquez un collaborateur ou demandez à
              l&apos;administrateur d&apos;augmenter le plafond avant d&apos;en inviter un nouveau.
            </p>
          ) : null}
        </div>
      </header>

      {invitedName !== null ? (
        <p role="status" className="text-sm text-foreground">
          Invitation envoyée à {invitedName}.
        </p>
      ) : null}

      {query.isPending ? (
        <p className="text-sm text-muted-foreground" role="status">
          Chargement de l&apos;annuaire…
        </p>
      ) : query.isError ? (
        <p className="text-sm text-destructive" role="alert">
          {errorMessage(query.error, 'Impossible de charger l’annuaire des contacts.')}
        </p>
      ) : (
        <TeamMasterDetail members={query.data.members} />
      )}

      {isInviteOpen ? (
        <InviteCollaboratorModal onSuccess={onInviteSuccess} onClose={() => setInviteOpen(false)} />
      ) : null}
    </section>
  );
}

/**
 * Point 2 — sous-titre COMPTEUR RÉEL. `cap === 0` (défaut V1) ⇒ « N collaborateurs · aucun plafond
 * défini. » (accord singulier/pluriel sur le VRAI nombre de contacts). `cap > 0` (R15-UX-02) ⇒ effectif
 * vivant « x / n » + badge « Plafond atteint ». Aucune couleur en dur, tokens sémantiques uniquement.
 */
function TeamSubtitle({
  contactCount,
  cap,
  liveCount,
  reached,
}: {
  readonly contactCount: number;
  readonly cap: number;
  readonly liveCount: number;
  readonly reached: boolean;
}): React.JSX.Element {
  if (cap <= 0) {
    const noun = `collaborateur${contactCount > 1 ? 's' : ''}`;
    return (
      <p className="text-sm text-muted-foreground">
        {contactCount} {noun} · aucun plafond défini.
      </p>
    );
  }
  return (
    <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
      <span>
        Collaborateurs : {liveCount} / {cap}
      </span>
      {reached ? (
        <span className="inline-flex items-center gap-1.5 rounded-md border border-status-suspended/40 bg-status-suspended-bg px-2 py-0.5 text-xs font-medium text-foreground">
          <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-status-suspended" />
          Plafond atteint
        </span>
      ) : null}
    </p>
  );
}

// =============================================================================
// Grille MASTER/DÉTAIL (points 4-12).
// =============================================================================

/** Filtre de statut porté par les chips exclusifs (point 5). */
type StatusFilter = 'all' | 'active' | 'revoked';

function TeamMasterDetail({
  members,
}: {
  readonly members: readonly SupplierTeamMemberDto[];
}): React.JSX.Element {
  // Préférences d'affichage : état LOCAL (jamais localStorage / URL — RULE_REF_006).
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('all');
  const [selectedId, setSelectedId] = React.useState<string | null>(null);

  // Statut du lien d'activation par contactId (tuile LIEN ÉTABLISSEMENT + affordance de renvoi).
  const tokenStatus = useSupplierTeamTokenStatus();
  const statusById = React.useMemo(() => {
    const map = new Map<string, TokenActivationStatusDto | null>();
    if (tokenStatus.data !== undefined) {
      for (const entry of tokenStatus.data) {
        map.set(entry.contactId, entry.status);
      }
    }
    return map;
  }, [tokenStatus.data]);

  // Tri stable de la liste : ordre alphabétique par nom puis prénom (le tri par colonne est SUPPRIMÉ).
  const sortedMembers = React.useMemo(
    () =>
      [...members].sort(
        (a, b) => a.nom.localeCompare(b.nom, 'fr') || a.prenom.localeCompare(b.prenom, 'fr'),
      ),
    [members],
  );

  const visibleMembers = React.useMemo(() => {
    const needle = normalize(search.trim());
    return sortedMembers.filter((member) => {
      if (statusFilter === 'active' && isRevoked(member)) {
        return false;
      }
      if (statusFilter === 'revoked' && !isRevoked(member)) {
        return false;
      }
      if (needle !== '') {
        const haystack = normalize(`${member.nom} ${member.prenom} ${member.email}`);
        if (!haystack.includes(needle)) {
          return false;
        }
      }
      return true;
    });
  }, [sortedMembers, search, statusFilter]);

  // Point 7 : le PREMIER contact (de la liste VISIBLE) est ouvert par défaut (dérivation PURE, sans
  // navigation). Si la sélection explicite sort du filtre courant (recherche / chip), la fiche bascule
  // sur le premier contact visible ; liste vide ⇒ aucune fiche.
  const selectedVisible = visibleMembers.find((m) => m.contactId === selectedId);
  const selectedMember = selectedVisible ?? visibleMembers[0];
  const effectiveSelectedId = selectedMember?.contactId ?? null;

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[360px_1fr] lg:items-start">
      {/* Colonne gauche : recherche + chips + liste (point 5-8). */}
      <Card className="flex min-h-[420px] flex-col overflow-hidden">
        <div className="flex flex-col gap-3 border-b border-border p-3">
          <Input
            type="search"
            aria-label="Rechercher un contact"
            placeholder="Rechercher un contact…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="h-9"
          />
          <div role="group" aria-label="Filtrer par statut" className="flex flex-wrap gap-2">
            <StatusChip
              pressed={statusFilter === 'all'}
              onSelect={() => setStatusFilter('all')}
            >
              Tous {members.length}
            </StatusChip>
            <StatusChip
              pressed={statusFilter === 'active'}
              onSelect={() => setStatusFilter('active')}
            >
              Actifs
            </StatusChip>
            <StatusChip
              pressed={statusFilter === 'revoked'}
              onSelect={() => setStatusFilter('revoked')}
            >
              Révoqués
            </StatusChip>
          </div>
        </div>
        <div
          aria-label="Liste des contacts de l’établissement"
          className="min-h-0 flex-1 overflow-y-auto"
        >
          {members.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">
              Aucun contact pour le moment. Invitez un premier collaborateur avec le bouton
              ci-dessus.
            </p>
          ) : visibleMembers.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground" role="status">
              Aucun contact ne correspond à votre recherche.
            </p>
          ) : (
            <ul className="flex flex-col divide-y divide-border">
              {visibleMembers.map((member) => (
                <li key={member.contactId}>
                  <ContactRow
                    member={member}
                    selected={member.contactId === effectiveSelectedId}
                    onSelect={() => setSelectedId(member.contactId)}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>

      {/* Colonne droite : fiche du contact sélectionné (points 9-12). */}
      <Card className="min-h-[420px] overflow-hidden">
        {selectedMember === undefined ? (
          <p className="p-6 text-sm text-muted-foreground">
            Sélectionnez un contact pour afficher sa fiche.
          </p>
        ) : (
          <ContactDetail
            member={selectedMember}
            linkStatus={
              statusById.has(selectedMember.contactId)
                ? (statusById.get(selectedMember.contactId) ?? null)
                : undefined
            }
            linkPending={tokenStatus.isPending}
          />
        )}
      </Card>
    </div>
  );
}

/** Chip de filtre exclusif (point 5) — bouton aria-pressed, un seul actif à la fois. */
function StatusChip({
  pressed,
  onSelect,
  children,
}: {
  readonly pressed: boolean;
  readonly onSelect: () => void;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onSelect}
      className={cn(
        'inline-flex h-8 items-center rounded-full border px-3 text-sm font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        pressed
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-input bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground',
      )}
    >
      {children}
    </button>
  );
}

/**
 * Une ligne de contact (point 6-8) : « Prénom NOM » (poids 500), sous-ligne « Rôle · email », point de
 * statut à droite (vert plein = actif / cercle gris vide = révoqué), TOUJOURS doublé d'un libellé texte
 * (sr-only) — jamais une couleur seule. Sélection : liseré ambre à gauche + fond ivoire (point 7). Le
 * nom accessible du bouton EST le nom du contact.
 */
function ContactRow({
  member,
  selected,
  onSelect,
}: {
  readonly member: SupplierTeamMemberDto;
  readonly selected: boolean;
  readonly onSelect: () => void;
}): React.JSX.Element {
  const revoked = isRevoked(member);
  return (
    <button
      type="button"
      aria-current={selected ? true : undefined}
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-3 border-l-2 px-4 py-3 text-left',
        'hover:bg-accent hover:text-accent-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
        selected ? 'border-l-primary bg-accent' : 'border-l-transparent',
      )}
    >
      <span className="min-w-0 flex-1">
        {/* Point 8 : le nom reste en NOIR (text-foreground) même pour un contact révoqué. */}
        <span className="block truncate font-medium text-foreground">{fullName(member)}</span>
        <span
          className={cn(
            'block truncate text-xs',
            revoked ? 'text-muted-foreground/80' : 'text-muted-foreground',
          )}
        >
          {roleLabel(member.role)} · {member.email}
        </span>
      </span>
      <span className="flex shrink-0 items-center">
        <span
          aria-hidden="true"
          className={cn(
            'h-2.5 w-2.5 rounded-full',
            revoked ? 'border border-muted-foreground/60' : 'bg-status-active',
          )}
        />
        <span className="sr-only">{revoked ? 'Révoqué' : 'Actif'}</span>
      </span>
    </button>
  );
}

// =============================================================================
// Fiche contact (points 9-12).
// =============================================================================

function ContactDetail({
  member,
  linkStatus,
  linkPending,
}: {
  readonly member: SupplierTeamMemberDto;
  /** Statut du lien d'activation : `undefined` = pas encore chargé, `null` = aucun lien émis. */
  readonly linkStatus: TokenActivationStatusDto | null | undefined;
  readonly linkPending: boolean;
}): React.JSX.Element {
  const revoke = useRevokeCollaborator();
  const [pendingRevoke, setPendingRevoke] = React.useState(false);

  const onConfirmRevoke = (): void => {
    revoke.mutate(
      { contactId: member.contactId, reason: 'Révocation par le responsable' },
      { onSettled: () => setPendingRevoke(false) },
    );
  };

  const state = stateBadge(member.state);

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Point 9-10 : en-tête (nom serif + badge de rôle + email) & actions à droite. */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-serif text-xl font-semibold tracking-tight">{fullName(member)}</h2>
            <RoleBadge role={member.role} />
          </div>
          <p className="text-sm text-muted-foreground">{member.email}</p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          {/* « Modifier le rôle » RETIRÉ (GATE_HUMAN 2026-09-12) : la gestion des rôles est une
              prérogative ADMIN, hors du périmètre fournisseur. Seule la révocation reste offerte au MASTER. */}
          {isRevocable(member) ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-destructive"
              aria-label={`Révoquer l'accès de ${member.prenom} ${member.nom}`}
              onClick={() => setPendingRevoke(true)}
            >
              Révoquer l&apos;accès
            </Button>
          ) : null}
        </div>
      </div>

      {/* Point 11 : trois tuiles bordées côte à côte. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <DetailTile label="STATUT DU CONTACT">
          <span className="inline-flex items-center gap-2 text-sm text-foreground">
            <span
              aria-hidden="true"
              className={cn('h-2 w-2 rounded-full', TONE_DOT_CLASS[state.tone])}
            />
            {state.label}
          </span>
        </DetailTile>

        <DetailTile label="LIEN ÉTABLISSEMENT">
          <LinkStatusValue status={linkStatus} pending={linkPending} />
          <ResendLinkAction member={member} status={linkStatus ?? null} />
        </DetailTile>

        <DetailTile label="CONNEXION">
          {member.lastActivityAt !== null ? (
            <span className="text-sm text-foreground">{formatLongDate(member.lastActivityAt)}</span>
          ) : (
            <span className="text-sm text-muted-foreground">Jamais connecté</span>
          )}
        </DetailTile>
      </div>

      {/* Point 12 : historique daté (le plus récent en tête ; uniquement les événements datés). */}
      <ContactHistory member={member} />

      <ConfirmDialog
        open={pendingRevoke}
        title="Révoquer ce collaborateur ?"
        description={`${member.prenom} ${member.nom} n’aura plus accès à la plateforme. Ses saisies déjà enregistrées sont conservées.`}
        confirmLabel={revoke.isPending ? 'Révocation…' : 'Révoquer'}
        confirmDisabled={revoke.isPending}
        onConfirm={onConfirmRevoke}
        onCancel={() => setPendingRevoke(false)}
      >
        {revoke.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {errorMessage(revoke.error, 'Échec de la révocation.')}
          </p>
        ) : null}
      </ConfirmDialog>
    </div>
  );
}

/** Badge de rôle (point 9) : Responsable = fond ambre clair ; Collaborateur = bordure fine (pas de plein). */
function RoleBadge({ role }: { readonly role: SupplierTeamRoleDto }): React.JSX.Element {
  if (role === 'MASTER') {
    return (
      <span className="inline-flex items-center rounded-md bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
        Responsable
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-md border border-border px-2 py-0.5 text-xs font-medium text-foreground">
      Collaborateur
    </span>
  );
}

/** Une tuile bordée avec un label mono MAJ (point 11). */
function DetailTile({
  label,
  children,
}: {
  readonly label: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="rounded-md border border-border p-3">
      <p className="font-mono text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <div className="mt-1.5 space-y-1.5">{children}</div>
    </div>
  );
}

/** Valeur de la tuile LIEN ÉTABLISSEMENT : badge texte, ou libellé gris explicite quand vide. */
function LinkStatusValue({
  status,
  pending,
}: {
  readonly status: TokenActivationStatusDto | null | undefined;
  readonly pending: boolean;
}): React.JSX.Element {
  if (status === undefined) {
    return (
      <span className="text-sm text-muted-foreground" role={pending ? 'status' : undefined}>
        {pending ? 'Chargement…' : 'Aucun lien'}
      </span>
    );
  }
  if (status === null) {
    return <span className="text-sm text-muted-foreground">Aucun lien</span>;
  }
  const badge = tokenStatusBadge(status);
  return (
    <span className="inline-flex items-center gap-2 text-sm text-foreground">
      <span aria-hidden="true" className={cn('h-2 w-2 rounded-full', TONE_DOT_CLASS[badge.tone])} />
      {badge.label}
    </span>
  );
}

/** Bloc HISTORIQUE (point 12) : liste datée en mono, plus récent en tête, dérivée des champs datés. */
function ContactHistory({ member }: { readonly member: SupplierTeamMemberDto }): React.JSX.Element {
  const events = React.useMemo(() => {
    const raw: ReadonlyArray<{ iso: string; label: string } | null> = [
      member.lastSubmissionAt !== null
        ? { iso: member.lastSubmissionAt, label: 'Dernière saisie de tarifs' }
        : null,
      member.invitedAt !== null ? { iso: member.invitedAt, label: 'Invitation envoyée' } : null,
      member.lastActivityAt !== null
        ? { iso: member.lastActivityAt, label: 'Dernière connexion' }
        : null,
    ];
    return raw
      .filter((e): e is { iso: string; label: string } => e !== null)
      .sort((a, b) => b.iso.localeCompare(a.iso));
  }, [member]);

  return (
    <section aria-labelledby="contact-history-title" className="space-y-2">
      <h3
        id="contact-history-title"
        className="font-mono text-[11px] font-medium uppercase tracking-wider text-muted-foreground"
      >
        Historique
      </h3>
      {events.length === 0 ? (
        <p className="text-sm text-muted-foreground">Aucun événement daté pour ce contact.</p>
      ) : (
        <ul className="space-y-1.5">
          {events.map((event) => (
            <li key={`${event.iso}-${event.label}`} className="flex items-baseline gap-4 text-sm">
              <span className="w-28 shrink-0 font-mono text-xs text-muted-foreground">
                {formatShortDate(event.iso)}
              </span>
              <span className="text-foreground">{event.label}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Action « Renvoyer le lien » d'un contact (Écart 1, conservée du câblage existant). Affordance FRONT :
 * actif seulement si le lien est « Expiré » ; désactivé pour « Envoyé » (encore valide) ; masqué pour
 * « Activé »/null. Les erreurs de renvoi sont gérées PAR CODE (429/409/404), jamais via le message
 * serveur brut. L'état de renvoi (pending/feedback) est LOCAL.
 */
function ResendLinkAction({
  member,
  status,
}: {
  readonly member: SupplierTeamMemberDto;
  readonly status: TokenActivationStatusDto | null;
}): React.JSX.Element | null {
  const resend: UseMutationResult<void, Error, string> = useResendSupplierActivation();
  const affordance = resendAffordance(status);
  const [pending, setPending] = React.useState(false);
  const [feedback, setFeedback] = React.useState<{
    tone: 'success' | 'error';
    message: string;
  } | null>(null);

  if (!affordance.render) {
    return null;
  }

  const name = `${member.prenom} ${member.nom}`;
  const label =
    affordance.disabled && affordance.disabledReason !== null
      ? `Renvoyer le lien d'activation à ${name} (${affordance.disabledReason})`
      : `Renvoyer le lien d'activation à ${name}`;

  const onResend = (): void => {
    setFeedback(null);
    setPending(true);
    resend.mutate(member.contactId, {
      onSuccess: () => {
        setFeedback({ tone: 'success', message: `Lien d'activation renvoyé à ${name}.` });
      },
      onError: (error) => {
        setFeedback({ tone: 'error', message: resendFailureMessage(resendFailureCode(error)) });
      },
      onSettled: () => {
        setPending(false);
      },
    });
  };

  return (
    <div className="space-y-1">
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={affordance.disabled || pending}
        aria-label={label}
        title={affordance.disabledReason ?? undefined}
        onClick={onResend}
      >
        {pending ? 'Envoi…' : 'Renvoyer le lien'}
      </Button>
      {feedback !== null ? (
        <p
          role={feedback.tone === 'error' ? 'alert' : 'status'}
          className={cn(
            'text-xs',
            feedback.tone === 'error' ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          {feedback.message}
        </p>
      ) : null}
    </div>
  );
}

// =============================================================================
// Modale d'invitation (§3.7 l.3090/3100) — IN-HOUSE, aucune dépendance ajoutée.
// =============================================================================
// Patron accessible : role="dialog" + aria-modal, focus déplacé sur le 1er champ à l'ouverture, PIÈGE
// de focus (Tab/Shift+Tab bouclent), fermeture Échap + clic overlay (décoratif), focus RENDU au bouton
// déclencheur à la fermeture. jsx-a11y conforme.

/** Sélecteur des éléments focusables (piège de focus WCAG 2.1.2 / 2.4.3). */
const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input:not([disabled]), select, [tabindex]:not([tabindex="-1"])';

interface InviteCollaboratorModalProps {
  /** Notifie le parent d'une invitation réussie (nom complet) → ferme + affiche le succès sur la page. */
  readonly onSuccess: (fullName: string) => void;
  /** Ferme le modal sans invitation (Annuler / Échap / clic overlay). */
  readonly onClose: () => void;
}

function InviteCollaboratorModal({
  onSuccess,
  onClose,
}: InviteCollaboratorModalProps): React.JSX.Element {
  const invite = useInviteCollaborator();
  const [nom, setNom] = React.useState('');
  const [prenom, setPrenom] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [validationError, setValidationError] = React.useState<string | null>(null);

  const panelRef = React.useRef<HTMLDivElement | null>(null);
  const firstFieldRef = React.useRef<HTMLInputElement | null>(null);
  const previouslyFocused = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    firstFieldRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }
      const root = panelRef.current;
      if (root === null) {
        return;
      }
      const focusables = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
      if (focusables.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement;
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      previouslyFocused.current?.focus();
    };
  }, [onClose]);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const trimmed = { nom: nom.trim(), prenom: prenom.trim(), email: email.trim() };
    if (trimmed.nom === '' || trimmed.prenom === '' || trimmed.email === '') {
      setValidationError('Renseignez le nom, le prénom et l’email du collaborateur.');
      return;
    }
    setValidationError(null);
    invite.mutate(
      { nom: trimmed.nom, prenom: trimmed.prenom, collaboratorEmail: trimmed.email },
      { onSuccess: () => onSuccess(`${trimmed.prenom} ${trimmed.nom}`) },
    );
  };

  const serverError =
    invite.isError && !invite.isPending
      ? errorMessage(invite.error, 'Échec de l’invitation.')
      : null;
  const feedback = validationError ?? serverError;

  const titleId = 'invite-modal-title';
  const descId = 'invite-modal-description';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay décoratif : ferme au clic, inerte pour les lecteurs d'écran. */}
      <div className="absolute inset-0 bg-foreground/40" aria-hidden="true" onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className={cn(
          'relative z-10 w-full max-w-lg space-y-4 rounded-lg border border-border',
          'max-h-[85vh] overflow-y-auto bg-card p-6 text-card-foreground shadow-lg',
        )}
      >
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <UserPlus aria-hidden="true" className="h-5 w-5 text-primary" />
            <h2 id={titleId} className="text-lg font-semibold tracking-tight">
              Inviter un collaborateur
            </h2>
          </div>
          <p id={descId} className="text-sm text-muted-foreground">
            L&apos;adresse email doit appartenir au même domaine que le vôtre (règle de sécurité
            §3.7). Un lien d&apos;activation lui sera envoyé.
          </p>
        </div>

        <form onSubmit={onSubmit} noValidate className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="invite-nom">Nom</Label>
            <Input
              ref={firstFieldRef}
              id="invite-nom"
              name="nom"
              autoComplete="family-name"
              value={nom}
              onChange={(e) => setNom(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="invite-prenom">Prénom</Label>
            <Input
              id="invite-prenom"
              name="prenom"
              autoComplete="given-name"
              value={prenom}
              onChange={(e) => setPrenom(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="invite-email">Email professionnel</Label>
            <Input
              id="invite-email"
              name="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-invalid={feedback !== null ? true : undefined}
              aria-describedby={feedback !== null ? 'invite-feedback' : undefined}
            />
          </div>

          {feedback !== null ? (
            <p id="invite-feedback" role="alert" className="text-sm text-destructive">
              {feedback}
            </p>
          ) : null}

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button type="submit" disabled={invite.isPending}>
              {invite.isPending ? 'Envoi…' : 'Envoyer l’invitation'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Message d'erreur : le texte serveur d'une SupplierTeamApiError, sinon un repli neutre. */
function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof SupplierTeamApiError) {
    return error.message;
  }
  return fallback;
}
